import React, { useState } from "react";
import {
  Input,
  Textarea,
  Checkbox,
  Spinner,
} from "@fluentui/react-components";
import {
  ShieldCheckmarkRegular,
  CheckmarkCircleRegular,
  SendRegular,
  MailRegular,
  CallRegular,
  LocationRegular,
  ArrowRightRegular,
  ChevronDownRegular,
} from "@fluentui/react-icons";
import "./EnterpriseSection.scss";

const PRODUCTS = [
  "AI-Powered CRM Suite",
  "Sovereign Email & Channels",
  "Hospitality Management System",
  "Academic Management Portal",
  "Agentic AI (Cael)",
  "Insurance & HMO Platform",
  "Custom Software Development",
];

const EnterpriseSection: React.FC = () => {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    company: "",
    product: PRODUCTS[0],
    message: "",
    newsletter: false,
  });
  const [status, setStatus] = useState<"idle" | "sending" | "sent">("idle");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterDone, setNewsletterDone] = useState(false);

  const ticketRef = `CIL-${String(Math.floor(Math.random() * 900000) + 100000)}`;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    setTimeout(() => setStatus("sent"), 1800);
  };

  return (
    <footer id="contact" className="ef">

      {/* ── ambient glows ── */}
      <div className="ef__glow ef__glow--a" aria-hidden />
      <div className="ef__glow ef__glow--b" aria-hidden />

      <div className="ef__inner">

        {/* ══ SECTION HEADER ══════════════════════════════════ */}
        <div className="ef__header">
          <div className="ef__header-left">
            <span className="ef__eyebrow">Get in touch</span>
            <h2 className="ef__title">
              Ready to build something<br />
              <span className="ef__title--accent">that scales?</span>
            </h2>
            <p className="ef__subtitle">
              Tell us what you're working on. Our team responds within 2 hours
              on business days.
            </p>

            {/* Contact details */}
            <div className="ef__contacts">
              <a href="mailto:support@cilpron.com" className="ef__contact">
                <span className="ef__contact-icon">
                  <MailRegular />
                </span>
                <div>
                  <span className="ef__contact-label">Email</span>
                  <span className="ef__contact-value">support@cilpron.com</span>
                </div>
              </a>
              <a href="tel:+2348122222455" className="ef__contact">
                <span className="ef__contact-icon">
                  <CallRegular />
                </span>
                <div>
                  <span className="ef__contact-label">Phone</span>
                  <span className="ef__contact-value">+234 (81) 2222-CILPRON</span>
                </div>
              </a>
              <div className="ef__contact">
                <span className="ef__contact-icon">
                  <LocationRegular />
                </span>
                <div>
                  <span className="ef__contact-label">Office</span>
                  <span className="ef__contact-value">Yaba, Lagos, Nigeria</span>
                </div>
              </div>
            </div>
          </div>

          {/* ══ FORM ════════════════════════════════════════════ */}
          <div className="ef__form-wrap">

            <div className="ef__ssl">
              <ShieldCheckmarkRegular />
              256-bit SSL — your data is encrypted
            </div>

            {status === "sent" ? (
              <div className="ef__success">
                <div className="ef__success-icon">
                  <CheckmarkCircleRegular />
                </div>
                <h3 className="ef__success-title">Message received</h3>
                <p className="ef__success-body">
                  Thanks, <strong>{form.fullName}</strong>. We've logged your
                  enquiry under reference{" "}
                  <span className="ef__success-ref">{ticketRef}</span> and will
                  be in touch within 2 hours.
                </p>
                <button
                  className="ef__btn ef__btn--outline"
                  onClick={() => setStatus("idle")}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="ef__form" noValidate>

                <div className="ef__row">
                  <div className="ef__field">
                    <label className="ef__label" htmlFor="ef-name">
                      Full name <span className="ef__req">*</span>
                    </label>
                    <Input
                      id="ef-name"
                      name="fullName"
                      required
                      value={form.fullName}
                      onChange={handleChange}
                      placeholder="Ada Okonkwo"
                      className="ef__input"
                    />
                  </div>
                  <div className="ef__field">
                    <label className="ef__label" htmlFor="ef-email">
                      Work email <span className="ef__req">*</span>
                    </label>
                    <Input
                      id="ef-email"
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="ada@company.com"
                      className="ef__input"
                    />
                  </div>
                </div>

                <div className="ef__row">
                  <div className="ef__field">
                    <label className="ef__label" htmlFor="ef-company">
                      Company
                    </label>
                    <Input
                      id="ef-company"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Acme Holdings Ltd"
                      className="ef__input"
                    />
                  </div>
                  <div className="ef__field">
                    <label className="ef__label" htmlFor="ef-product">
                      Product interest
                    </label>
                    <div className="ef__select-wrap">
                      <select
                        id="ef-product"
                        name="product"
                        value={form.product}
                        onChange={handleChange}
                        className="ef__select"
                      >
                        {PRODUCTS.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <ChevronDownRegular className="ef__select-icon" />
                    </div>
                  </div>
                </div>

                <div className="ef__field">
                  <label className="ef__label" htmlFor="ef-message">
                    How can we help?
                  </label>
                  <Textarea
                    id="ef-message"
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project, timeline, or any specific requirements..."
                    className="ef__textarea"
                    resize="vertical"
                  />
                </div>

                <Checkbox
                  id="ef-newsletter"
                  checked={form.newsletter}
                  onChange={(_, d) =>
                    setForm((p) => ({ ...p, newsletter: !!d.checked }))
                  }
                  label="Send me product updates and engineering insights — no spam."
                  className="ef__checkbox"
                />

                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="ef__btn ef__btn--primary"
                >
                  {status === "sending" ? (
                    <>
                      <Spinner size="tiny" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Send message
                      <SendRegular />
                    </>
                  )}
                </button>

              </form>
            )}
          </div>
        </div>

        {/* ══ FOOTER BOTTOM ═══════════════════════════════════ */}
        <div className="ef__bottom">
          <div className="ef__brand">
            <span className="ef__brand-name">CILPRON LTD</span>
            <p className="ef__copy">
              © {new Date().getFullYear()} CILPRON Limited. All rights reserved.
              RC-7395232.
            </p>
          </div>

          <div className="ef__newsletter">
            <span className="ef__newsletter-label">Monthly product digest</span>
            {newsletterDone ? (
              <p className="ef__newsletter-done">You're subscribed. Thanks!</p>
            ) : (
              <form
                className="ef__newsletter-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  setNewsletterDone(true);
                }}
              >
                <input
                  type="email"
                  required
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="ef__newsletter-input"
                />
                <button type="submit" className="ef__newsletter-btn">
                  <ArrowRightRegular />
                </button>
              </form>
            )}
          </div>
        </div>

      </div>
    </footer>
  );
};

export default EnterpriseSection;